import { supabase } from '../supabase';

// ============================================================================
// Auditoría de sesiones
// Registra en `auth_session_logs` cada inicio ('login') y cierre ('logout') de
// sesión. Es best-effort: NUNCA debe bloquear ni romper el login/logout si el
// insert falla (RLS, red, etc.), por eso todo va dentro de un try/catch que solo
// loguea un warning.
//
// El RLS de la tabla solo deja insertar filas con user_id = auth.uid(), así que
// un usuario únicamente puede registrar su propio evento. La lectura está
// reservada al super admin (ver migración 20260603000000_auth_session_logs.sql).
// ============================================================================

/**
 * @param {'login'|'logout'} eventType
 * @param {{ user?: object, profile?: object, organization?: object }} [ctx]
 *   Datos opcionales ya disponibles en memoria para evitar queries extra.
 */
export async function logAuthEvent(eventType, ctx = {}) {
  try {
    const authUser = ctx.user || (await supabase.auth.getUser())?.data?.user;
    if (!authUser?.id) return;

    // Profile (full_name / organization_id). Si no nos lo pasaron, lo buscamos.
    let profile = ctx.profile || null;
    if (!profile) {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, email, organization_id, role')
        .eq('id', authUser.id)
        .maybeSingle();
      profile = data || null;
    }

    // Nombre de la organización (snapshot legible para la bitácora).
    let organizationName = ctx.organization?.name || null;
    const organizationId = profile?.organization_id || ctx.organization?.id || null;
    if (!organizationName && organizationId) {
      const { data: org } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', organizationId)
        .maybeSingle();
      organizationName = org?.name || null;
    }

    await supabase.from('auth_session_logs').insert({
      user_id: authUser.id,
      email: profile?.email || authUser.email || null,
      full_name: profile?.full_name || null,
      organization_id: organizationId,
      organization_name: organizationName,
      role: profile?.role || null,
      event_type: eventType,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
  } catch (error) {
    // La auditoría es secundaria: si falla, seguimos sin molestar al usuario.
    console.warn('[sessionAudit] no se pudo registrar el evento de sesión', error);
  }
}

// ============================================================================
// Auditoría de navegación
// Registra en `navigation_logs` cada clic en un módulo del sidebar. Igual que
// logAuthEvent: best-effort, nunca bloquea la navegación. Se llama desde
// Sistema.jsx (onNavigate), que ya tiene user/profile/organization en memoria,
// así que NO dispara queries extra por clic.
// ============================================================================

// Dedupe: sin esto, cada ida y vuelta entre dos pantallas escribe una fila y la
// bitacora se vuelve ilegible (y cara) en pocos dias. Registramos un modulo por
// usuario cuando cambia respecto del anterior, y como mucho una vez por ventana
// aunque el usuario entre y salga del mismo lugar.
const NAV_DEDUPE_MS = 5 * 60 * 1000;
const ultimaNavegacion = { moduleId: null, at: 0 };

const debeRegistrarNavegacion = (moduleId) => {
  const ahora = Date.now();
  if (
    ultimaNavegacion.moduleId === moduleId &&
    ahora - ultimaNavegacion.at < NAV_DEDUPE_MS
  ) {
    return false;
  }
  ultimaNavegacion.moduleId = moduleId;
  ultimaNavegacion.at = ahora;
  return true;
};

/** Reinicia el dedupe al cerrar sesion, para no perder la primera navegacion del proximo usuario. */
export const resetNavigationAudit = () => {
  ultimaNavegacion.moduleId = null;
  ultimaNavegacion.at = 0;
};

/**
 * @param {string} moduleId  id del módulo del sidebar (ej. 'ventas-pos')
 * @param {{ user?: object, profile?: object, organization?: object }} [ctx]
 */
export async function logNavigation(moduleId, ctx = {}) {
  try {
    if (!moduleId) return;
    if (!debeRegistrarNavegacion(moduleId)) return;
    const authUser = ctx.user || (await supabase.auth.getUser())?.data?.user;
    if (!authUser?.id) return;

    const profile = ctx.profile || null;
    const organizationId = profile?.organization_id || ctx.organization?.id || null;
    const organizationName = ctx.organization?.name || null;

    await supabase.from('navigation_logs').insert({
      user_id: authUser.id,
      email: profile?.email || authUser.email || null,
      full_name: profile?.full_name || null,
      organization_id: organizationId,
      organization_name: organizationName,
      role: profile?.role || null,
      module_id: moduleId,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    });
  } catch (error) {
    console.warn('[sessionAudit] no se pudo registrar la navegación', error);
  }
}
